import Select from '../node_modules/ol/interaction/Select.js';
import Modify from '../node_modules/ol/interaction/Modify.js';
import {shiftKeyOnly, singleClick} from '../node_modules/ol/events/condition.js';

import FeatureStyleControls from './FeatureStyleControls.js';
import {map} from './index.js';
import editableAttributes from './editableAttributes.js';
import Tooltip from './Tooltip.js';
import postFeatureToServer from './postFeatureToServer.js';  // this is really used despite what vscode thinks!
import Snap from './Snap.js'

/**
 * Constructor
 *
 *  1. change the style (done in the selectInteraction)
 *    a. selected object's styles are copied to UI
 *    b. when changes are made in the UI those styles need to be copied back to selected object
 *  2a. modify the geometry (done in modifyInteraction)
 *  2b. when geometry of a measure object is changed need to update the area or length
 */

var styleControls;  // exported below

// constructor
export default function() {

  styleControls = new FeatureStyleControls(); // 2023-12-29 added "new"

  let selectInteraction = new Select({
    // style only works with features styled by layer, not with features styled individually
    // http://stackoverflow.com/questions/40289416/select-interaction-style-not-used-when-feature-has-its-own-style
    style: null,  // important in ol6
    // layers: layersFilter  // https://openlayers.org/en/v5.3.0/apidoc/module-ol_interaction_Select.html
    // features: collection // Collection where the interaction will place selected features. Optional. If not set the interaction will create a collection
  });
  selectInteraction.on('select', evt => {
    deselect(evt.deselected);
    select(evt.selected);
  });
  selectInteraction.deselectAll = deselect;

  rwg.selection.selectInteraction = selectInteraction;

  let modifyInteraction = new Modify({
    features: selectInteraction.getFeatures(),
    deleteCondition: function(event) {  // default delete action is a single click
      return shiftKeyOnly(event) && singleClick(event);
    }
  });

  let snap = new Snap(map)

  function getLayerStyle(feature) {
    let style = null;
    let res = map.getView().getResolution();
    // let featuresLayer = feature.getLayer(map); // getLayer() must be added to ol.Feature.prototype
    let featuresLayer = selectInteraction.getLayer(feature);  // native ol way to get feature's layer
    // console.log(`selected feature's layer: ${featuresLayer.get('title')}, z-index: ${featuresLayer.getZIndex()}`);
    if (featuresLayer) {  // selecting a vertex in a line or poly will cause selectInteraction.getLayer() return null
      if (typeof featuresLayer.getStyleFunction() === 'function') {
        style = featuresLayer.getStyleFunction()(feature, res); // invoke the layer's style function
      } else {
        style = featuresLayer.getStyle()(feature, res);
      }
    }
    return style;
  }

  /* this DOES NOT WORK on MultiPolygons! */
  function applySelectStyle(feature) {
    if (feature.getGeometry().getType().includes('Multi')) {
      console.error(`applySelectStyle() does not work with 'Multi' geometries`)
    }
    let style = feature.getStyle();  // will be null or an array of styles
    if (style === null) { // if feature doesn't have a style get the feature's layer's style
      // if style is static getLayerStyle() returns an array but if function returns single object?
      let lyrStyle = getLayerStyle(feature);
      if (lyrStyle) {
        if (Array.isArray(lyrStyle)) { lyrStyle = lyrStyle[0] }
        style = [ lyrStyle.clone() ];
        feature.setStyle([style[0]].concat(rwg.styles.get('selectedStyle')));
      }
      feature.set('hasOwnStyle_', false);
    } else {
      feature.setStyle([feature.getStyle()[0]].concat(rwg.styles.get('selectedStyle')));
      feature.set('hasOwnStyle_', true);
    }
    if (style) {  // style will be null if selecting a vertex of a selected polygon
      styleControls.setControls(feature, selectInteraction);  // set style control values to match feature's styles
    }
  }

  // this is the main function, it is called when a feature has been selected from the map
  function select(features) {
    let index = features.length - 1;  // don't mutate an array in a forEach loop so use "while" instead of "forEach", although shouldn't be mutating here anyway
    while (index >= 0) {
      let feature = features[index];
      let geom = feature.getGeometry();
      let attribElement = document.getElementById('feature-attributes');

      if (typeof feature === 'undefined') return; // nothing selected, nothing to do

      feature.set('revision0_', feature.getRevision());
      applySelectStyle(feature); // this also sets style controls
      attribElement.innerHTML = '';
      attribElement.appendChild(editableAttributes(feature));
      let labelElement = document.querySelector('[name=label]');
      if (labelElement) labelElement.focus();

      if (feature.get('tooltipOverlay_') === undefined) {
        feature.set('tooltipOverlay_', new Tooltip(map));
        feature.get('tooltipOverlay_').update(geom, 'create')
      } else {
        feature.get('tooltipOverlay_').update(geom, 'modify');
      }

      if (feature.get('hasGeomChangeHandler_') === undefined) {
        feature.on('change', function (evt) { // eslint-disable-line no-unused-vars
          if (feature.get('tooltipOverlay_')) {
            feature.get('tooltipOverlay_').update(geom, 'modify');
          }
        });
        feature.set('hasGeomChangeHandler_', true); // rwg flag so that handler is only added once
      }
      index -= 1;
    }
    rwg.sidePanel.show();
  }  // select

  /**
   * call with an array of features to be deselected or
   * call w/o a param to deselect all features
   */
  function deselect(f) {
    let features = f ? f : selectInteraction.getFeatures().getArray();
    let index = features.length - 1;
    let featuresToWrite = [];

    // don't mutate an array in a forEach loop so use "while" instead of "forEach"
    // https://stackoverflow.com/questions/24812930/how-to-remove-element-from-array-in-foreach-loop
    // features.forEach(function(feature) {
    while (index >= 0) {
      let feature = features[index];
      // need to check revisions here because the style stuff below will fire a couple change events
      let revised = (feature.getRevision() - feature.get('revision0_') > 1 ) ? true : false;
      let style = feature.get('hasOwnStyle_') ? [feature.getStyle()[0]] : null;
      if (rwg.config.thematic && style) {
        let thematicField = rwg.config.thematic.getThematicField();
        if (thematicField) {
          let val;
          if (thematicField.indexOf('_') === 0) {
            let arr = thematicField.split('-');
            val = feature.get(arr[0])[arr[1]];
          } else {
            val = feature.get(thematicField);
          }
          rwg.config.thematic.setThematicStyle(val, style);
        }
        // just using the feature's style to transfer the style that was set in FeatureStyleControls.js
        // to thematic, but don't want feature to retain its own unique style when doing a thematic map
        style = null;
        feature.set('hasOwnStyle_', false);
      }
      feature.setStyle(style);

      if (revised) {
        featuresToWrite.push(feature);
      }
      selectInteraction.getFeatures().remove(feature);
      index -= 1;

      // remove tooltip
      let tooltipOverlay_ = feature.get('tooltipOverlay_');
      if (tooltipOverlay_) {
        tooltipOverlay_.remove();
        feature.unset('tooltipOverlay_')
      }
    } // while()

    if (rwg.config.saveFeatures && featuresToWrite.length) {
      postFeatureToServer('update', featuresToWrite);
    }
    // if there's no feature selected switch to the sidebar legend tab
    if (selectInteraction.getFeatures().getLength() === 0) {
      document.querySelector('#featureDiv').style.display = 'none';
      if (document.querySelector('#drawHelp')) {
        document.querySelector('#drawHelp').style.display = 'block'
      }
    }
  } // deselect

  // switchboard
  return function(mode) {   // on|off|clear
    if (mode === 'off') {
      deselect();
      if (selectInteraction) {
        selectInteraction.getFeatures().clear();
        map.removeInteraction(selectInteraction);
      }
      if (modifyInteraction) {
        map.removeInteraction(modifyInteraction);
      }
      snap('off')
      document.getElementById('map-div').style.cursor = 'auto';
    } else if (mode === 'on') {
      map.addInteraction(selectInteraction);
      map.addInteraction(modifyInteraction);
      snap('on')
    } else if (mode === 'clear') {
      if (selectInteraction) {
        selectInteraction.getFeatures().clear();
      }
    }/*  else if (mode === 'deselect') {
      deselect();
    } */
  };
}

export {styleControls};
